import { Typography } from "@circle-react-uikit/Typography";
import type { CompassModalProps } from "@circle-react/components/SettingsApp/CompassCompat/modalHelpers";
import { Modal } from "@circleco/compass/components/Modal";
import { useRef } from "react";

import { t } from "@/i18n-js/instance";

import { Loader } from "../../Loader";
import { BlockingFeatures } from "../BlockingFeatures";
import { useManagePlanTrialModalLogic } from "./useManagePlanTrialModalLogic";

export const ManagePlanTrialModalComponent = ({
  open,
  onOpenChange,
}: CompassModalProps) => {
  const {
    isCancelled,
    isLoading,
    isCheckingDowngrade,
    hasBlockers,
    canDowngradeData,
    currentPlan,
    planTrialEndDateFormatted,
    planTrialDaysRemainingInWords,
    trackModalEvent,
    destroyPlanTrial,
  } = useManagePlanTrialModalLogic({ isVisible: open });

  const actionTakenRef = useRef(false);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      if (!actionTakenRef.current) {
        trackModalEvent("manage_plan_trial_modal.closed", {
          was_cancelled: isCancelled,
        });
      }
      actionTakenRef.current = false;
      onOpenChange(false);
    }
  };

  const handleKeepTrial = () => {
    actionTakenRef.current = true;
    trackModalEvent("manage_plan_trial_modal.keep_trial.clicked");
    onOpenChange(false);
  };

  const handleCancelTrial = () => {
    trackModalEvent("manage_plan_trial_modal.cancel_trial.clicked", {
      cannot_downgrade: canDowngradeData && !canDowngradeData.can_downgrade,
    });
    void destroyPlanTrial();
  };

  const handleBackToCommunity = () => {
    actionTakenRef.current = true;
    trackModalEvent("manage_plan_trial_modal.back_to_community.clicked");
    onOpenChange(false);
  };

  const renderModalContent = () => {
    if (isCheckingDowngrade || (isLoading && !isCancelled)) {
      return (
        <Modal.Body>
          <Loader
            title={
              isLoading
                ? t("manage_plan_trial_modal.canceling_trial")
                : t("pricing_plans.upgrade_modal.loading.validating_limits")
            }
          />
        </Modal.Body>
      );
    }

    if (isCancelled) {
      return (
        <>
          <Modal.Header
            title={t("manage_plan_trial_modal.cancelled_title")}
            showCloseButton
          />
          <Modal.Body>
            <div className="py-6 text-center">
              <Typography.BodyLg>
                {t("manage_plan_trial_modal.cancelled_message", {
                  planName: currentPlan?.display_name,
                })}
              </Typography.BodyLg>
            </div>
          </Modal.Body>
          <Modal.Footer
            primaryAction={{
              label: t("manage_plan_trial_modal.back_to_community_button"),
              onClick: handleBackToCommunity,
            }}
          />
        </>
      );
    }

    return (
      <>
        <Modal.Header
          title={t("manage_plan_trial_modal.title")}
          showCloseButton
        />
        <Modal.Body>
          <div className="mb-6 space-y-4">
            <Typography.BodyMd>
              {t("manage_plan_trial_modal.trial_description", {
                daysRemaining: planTrialDaysRemainingInWords,
                trialEndDate: planTrialEndDateFormatted,
              })}
            </Typography.BodyMd>

            {hasBlockers &&
              canDowngradeData?.blocking_features &&
              canDowngradeData.blocking_features.length > 0 && (
                <BlockingFeatures
                  blockingFeatures={canDowngradeData.blocking_features}
                  i18nRoot="pricing_plans.upgrade_modal.interval_switch"
                />
              )}
          </div>
        </Modal.Body>
        <Modal.Footer
          secondaryAction={{
            label: t("manage_plan_trial_modal.cancel_trial_button"),
            onClick: handleCancelTrial,
          }}
          primaryAction={{
            label: t("manage_plan_trial_modal.keep_trial_button"),
            onClick: handleKeepTrial,
          }}
        />
      </>
    );
  };

  return (
    <Modal.Root open={open} onOpenChange={handleOpenChange} size="md">
      <Modal.Content>{renderModalContent()}</Modal.Content>
    </Modal.Root>
  );
};
